import { useMemo, useRef, useState, KeyboardEvent } from 'react';
import { ArrowUp, Loader2 } from 'lucide-react';

interface ChatInputProps {
  onSend: (message: string) => void;
  disabled?: boolean;
  isLoading?: boolean;
  placeholder?: string;
}

export function ChatInput({ onSend, disabled = false, isLoading = false, placeholder }: ChatInputProps) {
  const [message, setMessage] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // On touch devices the return key inserts a newline and only the send
  // button sends (Enter-to-send is a hardware-keyboard convention).
  const isTouch = useMemo(
    () => typeof window !== 'undefined' && window.matchMedia('(pointer: coarse)').matches,
    []
  );

  const handleSend = () => {
    const trimmedMessage = message.trim();
    if (trimmedMessage && !disabled && !isLoading) {
      onSend(trimmedMessage);
      setMessage('');
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !isTouch) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(e.target.value);
    // Auto-resize textarea
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 160)}px`;
    }
  };

  return (
    <div className="flex-shrink-0 border-t border-gray-200 bg-white px-3 pt-2 pb-[max(0.625rem,env(safe-area-inset-bottom))] dark:border-gray-700 dark:bg-gray-800 md:p-4">
      <div className="mx-auto flex max-w-4xl items-end gap-2">
        {/* Stays enabled while a reply is loading so the phone keyboard
            doesn't dismiss between messages; handleSend gates the actual send. */}
        <textarea
          ref={textareaRef}
          value={message}
          onChange={handleInput}
          onKeyDown={handleKeyDown}
          placeholder={placeholder || 'Ask about a protocol'}
          disabled={disabled}
          rows={1}
          className="min-w-0 flex-1 resize-none rounded-2xl border border-gray-300 bg-white px-4 py-2.5 text-base text-gray-900 placeholder-gray-400 transition-colors duration-150 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:bg-gray-100 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 dark:placeholder-gray-400 dark:disabled:bg-gray-800 sm:text-sm"
          style={{ minHeight: '44px', maxHeight: '160px' }}
        />
        {/* pointerDown is prevented so tapping send never blurs the textarea
            (a blur closes the phone keyboard mid-conversation) */}
        <button
          onClick={handleSend}
          onPointerDown={(e) => e.preventDefault()}
          disabled={!message.trim() || disabled || isLoading}
          className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-primary-600 text-white transition-colors duration-150 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:bg-gray-300 dark:disabled:bg-gray-600 sm:h-10 sm:w-10"
          aria-label="Send message"
        >
          {isLoading ? (
            <Loader2 size={19} className="animate-spin" />
          ) : (
            <ArrowUp size={19} strokeWidth={2.5} />
          )}
        </button>
      </div>
      <p className="mt-2 hidden text-center text-xs text-gray-500 dark:text-gray-400 sm:block">
        Press Enter to send, Shift+Enter for new line
      </p>
    </div>
  );
}
